class UgCommonPopup extends HTMLElement {
  constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      
      // 回调函数
      this.beforeOpen = null;
      this.afterClose = null;
      
      // 简化的滚动状态保存
      this.originalScrollTop = 0;
      this.originalBodyStyle = {};
      
      // 创建样式
      this.createStyles();
      
      // 创建DOM结构
      this.createStructure();
      
      // 绑定事件
      this.bindEvents();
      
      // 初始状态为隐藏
      this.isVisible = false;
  }
  
  createStyles() {
      const style = document.createElement('style');
      style.textContent = `
          :host {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              z-index: 1000;
              display: none;
              justify-content: center;
              align-items: center;
          }
          
          :host([visible]) {
              display: flex;
          }
          
          .ug-common-popup-overlay {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: rgba(0, 0, 0, 0.5);
              backdrop-filter: blur(2px);
              cursor: pointer;
          }
          
          .ug-common-popup-container {
              position: relative;
              background: white;
              border-radius: 8px;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
              max-width: 90vw;
              max-height: 90vh;
              overflow: auto;
              cursor: default;
              animation: ugCommonPopupFadeIn 0.3s ease-out;
              z-index: 1001;
          }
          
          .ug-common-popup-content {
              padding: 0;
          }
          
          .ug-common-popup-close-button {
              position: absolute;
              top: 15px;
              right: 15px;
              background: none;
              border: none;
              font-size: 24px;
              cursor: pointer;
              color: #666;
              width: 30px;
              height: 30px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s ease;
              z-index: 1002;
          }
          
          .ug-common-popup-close-button:hover {
              background-color: #f0f0f0;
              color: #333;
          }
          
          @keyframes ugCommonPopupFadeIn {
              from {
                  opacity: 0;
                  transform: scale(0.9) translateY(-20px);
              }
              to {
                  opacity: 1;
                  transform: scale(1) translateY(0);
              }
          }
          
          /* 可定制的CSS变量 */
          .ug-common-popup-container {
              background: var(--ug-common-popup-bg, white);
              border-radius: var(--ug-common-popup-border-radius, 8px);
              box-shadow: var(--ug-common-popup-shadow, 0 10px 25px rgba(0, 0, 0, 0.2));
              max-width: var(--ug-common-popup-max-width, 90vw);
              max-height: var(--ug-common-popup-max-height, 90vh);
          }
          
          .ug-common-popup-overlay {
              background-color: var(--ug-common-popup-overlay-bg, rgba(0, 0, 0, 0.5));
          }
          
          .ug-common-popup-close-button {
              color: var(--ug-common-popup-close-color, #666);
              font-size: var(--ug-common-popup-close-size, 30px);
          }
      `;
      this.shadowRoot.appendChild(style);
  }
  
  createStructure() {
      const container = document.createElement('div');
      container.innerHTML = `
          <div class="ug-common-popup-overlay"></div>
          <div class="ug-common-popup-container">
              <button class="ug-common-popup-close-button" aria-label="关闭">&times;</button>
              <div class="ug-common-popup-content">
                  <slot></slot>
              </div>
          </div>
      `;
      this.shadowRoot.appendChild(container);
      
      this.overlay = this.shadowRoot.querySelector('.ug-common-popup-overlay');
      this.closeButton = this.shadowRoot.querySelector('.ug-common-popup-close-button');
      this.container = this.shadowRoot.querySelector('.ug-common-popup-container');
  }
  
  bindEvents() {
      // 点击背景关闭
      this.overlay.addEventListener('click', () => {
          this.close();
      });
      
      // 点击关闭按钮
      this.closeButton.addEventListener('click', () => {
          this.close();
      });
      
      // 阻止容器点击事件冒泡
      this.container.addEventListener('click', (e) => {
          e.stopPropagation();
      });
      
      // ESC键关闭
      this.handleKeyDown = (e) => {
          if (e.key === 'Escape' && this.isVisible) {
              this.close();
          }
      };
  }
  
  // 简化的禁止body滚动方法
  disableBodyScroll() {
      // 记录当前滚动位置
      this.originalScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // 保存body原始样式
      const body = document.body;
      this.originalBodyStyle = {
          overflow: body.style.overflow,
          position: body.style.position,
          top: body.style.top,
          width: body.style.width
      };
      
      // 设置固定样式防止滚动
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${this.originalScrollTop}px`;
      body.style.width = '100%';
  }
  
  // 简化的恢复body滚动方法
  enableBodyScroll() {
      const body = document.body;
      
      // 恢复原始样式
      body.style.overflow = this.originalBodyStyle.overflow;
      body.style.position = this.originalBodyStyle.position;
      body.style.top = this.originalBodyStyle.top;
      body.style.width = this.originalBodyStyle.width;
      
      // 恢复滚动位置
      window.scrollTo(0, this.originalScrollTop);
  }
  
  // 打开弹窗
  open() {
      // 执行打开前回调
      if (this.beforeOpen && typeof this.beforeOpen === 'function') {
          const result = this.beforeOpen();
          if (result === false) return; // 如果返回false，阻止打开
      }
      
      this.isVisible = true;
      this.setAttribute('visible', '');
      
      // 禁止外部滚动并记录位置
      this.disableBodyScroll();
      
      // 添加键盘事件监听
      document.addEventListener('keydown', this.handleKeyDown);
      
      // 分发自定义事件
      this.dispatchEvent(new CustomEvent('ug-common-popup-opened', { 
          bubbles: true,
          detail: { popup: this }
      }));
  }
  
  // 关闭弹窗
  close() {
      this.isVisible = false;
      this.removeAttribute('visible');
      
      // 恢复外部滚动和原始位置
      this.enableBodyScroll();
      
      // 移除键盘事件监听
      document.removeEventListener('keydown', this.handleKeyDown);
      
      // 执行关闭后回调
      if (this.afterClose && typeof this.afterClose === 'function') {
          this.afterClose();
      }
      
      // 分发自定义事件
      this.dispatchEvent(new CustomEvent('ug-common-popup-closed', { 
          bubbles: true,
          detail: { popup: this }
      }));
  }
  
  // 设置回调函数的方法
  setBeforeOpen(callback) {
      this.beforeOpen = callback;
  }
  
  setAfterClose(callback) {
      this.afterClose = callback;
  }
  
  // 属性观察
  static get observedAttributes() {
      return ['visible'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'visible') {
          this.isVisible = newValue !== null;
      }
  }
}

// 注册自定义元素
customElements.define('ug-common-popup', UgCommonPopup);